const LIVE_OFFER_STATUSES = new Set(["live", "approved"]);

const DEFAULT_LIVE_SAFE_EDIT_FIELDS = new Set([
  "category",
  "subCategory",
  "title",
  "description",
  "redemptionFrequency",
  "redemptionDays",
  "startDate",
  "endDate",
  "startTime",
  "endTime",
]);

const MERCHANT_LIVE_SAFE_EDIT_FIELDS = new Set([
  "category",
  "subCategory",
  "title",
  "description",
  "necessaryRule",
  "redemptionFrequency",
  "redemptionDays",
  "startDate",
  "endDate",
  "startTime",
  "endTime",
]);

const DECLINED_STYLE_OFFER_STATUSES = new Set([
  "declined",
  "disabled",
  "rejected",
]);

const AUTO_GENERATED_FREQUENCY_RULE_TITLES = new Set([
  "may be redeemed once",
  "once daily",
  "once every 30 days",
  "once every 90 days",
  "once every 120 days",
]);

const LIVE_OFFER_COMPARISON_FIELDS = new Set([
  "title",
  "description",
  "category",
  "subCategory",
  "offerType",
  "offerDiscountType",
  "onlineOption",
  "discountRange",
  "fixedDollarAmount",
  "offerCodes",
  "websiteURL",
  "thumbnailImage",
  "previewImages",
  "ruleAndRegulation",
  "locations",
  "availableAcrossAllStores",
  "redemptionFrequency",
  "redemptionDays",
  "redemptionMethod",
  "startTime",
  "endTime",
  "callback_url",
  "document",
  "discountTitle",
  "necessaryRule",
  "startDate",
  "endDate",
]);

const normalizeOfferType = (offerType) => {
  const normalized = String(offerType || "").toUpperCase();
  if (
    normalized === "INSTORE_OFFER" ||
    normalized === "INSTORE_SWOODLE_OFFER"
  ) {
    return "INSTORE";
  }
  if (normalized === "ONLINE_OFFER") {
    return "ONLINE";
  }
  return normalized;
};

const normalizeRole = (role) => String(role || "").toLowerCase();

export const isMerchantOfferEditor = (role) =>
  normalizeRole(role) === "merchant";

const getComparisonContext = (currentPayload, originalOffer) => {
  const offerType = normalizeOfferType(
    currentPayload?.offerType || originalOffer?.offerType,
  );
  const discountType = String(
    currentPayload?.offerDiscountType || originalOffer?.offerDiscountType || "",
  ).toUpperCase();

  return { offerType, discountType };
};

const shouldIgnoreField = (field, currentPayload, originalOffer) => {
  const { offerType, discountType } = getComparisonContext(
    currentPayload,
    originalOffer,
  );

  if (field === "discountRange" && discountType !== "DISCOUNTED_PERCENT") {
    return true;
  }

  if (field === "fixedDollarAmount" && discountType !== "FIXED_DOLLAR") {
    return true;
  }

  if (
    (field === "onlineOption" ||
      field === "websiteURL" ||
      field === "offerCodes") &&
    offerType !== "ONLINE_OFFER"
  ) {
    return true;
  }

  if (
    (field === "locations" || field === "availableAcrossAllStores") &&
    !offerType.startsWith("INSTORE")
  ) {
    return true;
  }

  return false;
};

const normalizeRuleComparisonValue = (item) => {
  if (item == null) return "";

  if (typeof item === "object") {
    return String(item?.title || item?.name || item?._id || item?.id || "")
      .trim()
      .toLowerCase();
  }

  return String(item).trim().toLowerCase();
};

const shouldIgnoreAutoFrequencyRule = (item) =>
  AUTO_GENERATED_FREQUENCY_RULE_TITLES.has(normalizeRuleComparisonValue(item));

const getCanonicalLiveOfferPayload = (payload, role) => {
  const canonical = { ...(payload || {}) };
  const offerType = normalizeOfferType(canonical?.offerType);
  const discountType = String(canonical?.offerDiscountType || "").toUpperCase();

  if (offerType) {
    canonical.offerType = offerType;
  }

  if (!canonical.locations && Array.isArray(canonical.tempLocations)) {
    canonical.locations = canonical.tempLocations;
  }

  if (!canonical.ruleAndRegulation && Array.isArray(canonical.rules)) {
    canonical.ruleAndRegulation = canonical.rules;
  }

  if (
    Array.isArray(canonical.ruleAndRegulationForComparison) &&
    canonical.ruleAndRegulationForComparison.length > 0
  ) {
    canonical.ruleAndRegulation = canonical.ruleAndRegulationForComparison;
  }

  if (
    isMerchantOfferEditor(role) &&
    Array.isArray(canonical.ruleAndRegulation)
  ) {
    canonical.ruleAndRegulation = canonical.ruleAndRegulation.filter(
      (rule) => !shouldIgnoreAutoFrequencyRule(rule),
    );
  }

  if (discountType !== "DISCOUNTED_PERCENT") {
    delete canonical.discountRange;
  }

  if (discountType !== "FIXED_DOLLAR") {
    delete canonical.fixedDollarAmount;
  }

  if (offerType !== "ONLINE_OFFER") {
    delete canonical.onlineOption;
    delete canonical.websiteURL;
    delete canonical.offerCodes;
  }

  delete canonical.tempLocations;
  delete canonical.rules;
  delete canonical.ruleAndRegulationForComparison;
  delete canonical.rulesStepTouched;

  return canonical;
};

const normalizeOfferValue = (field, value) => {
  if (value == null) return "";

  if (
    field === "description" ||
    field === "title" ||
    field === "websiteURL" ||
    field === "callback_url" ||
    field === "discountTitle" ||
    field === "necessaryRule" ||
    field === "redemptionMethod"
  ) {
    return String(value).trim();
  }

  if (field === "category" && typeof value === "object") {
    return value?._id || value?.id || "";
  }

  if (field === "availableAcrossAllStores") {
    return value === true || value === "true" ? "true" : "";
  }

  if (field === "fixedDollarAmount" || field === "discountRange") {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue === 0) return "";
    return String(numericValue);
  }

  if (field === "startDate" || field === "endDate") {
    const str = String(value).trim();
    if (!str) return "";
    return str.slice(0, 10);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "object") {
          return (
            item?._id || item?.id || item?.title || item?.name || String(item)
          );
        }
        return String(item);
      })
      .filter(Boolean)
      .sort()
      .join(",");
  }

  if (typeof value === "object") {
    return value?._id || value?.id || JSON.stringify(value);
  }

  return String(value);
};

export const isLiveOfferStatus = (status) =>
  LIVE_OFFER_STATUSES.has(String(status || "").toLowerCase());

export const isDeclinedStyleOfferStatus = (status) =>
  DECLINED_STYLE_OFFER_STATUSES.has(String(status || "").toLowerCase());

export const resolveOfferLiveState = ({
  isApprovedProp,
  editOfferData,
  offerData,
}) => {
  if (typeof isApprovedProp === "boolean") {
    return isApprovedProp;
  }

  return isLiveOfferStatus(editOfferData?.status || offerData?.status);
};

const getLiveOfferSafeFields = (role) =>
  isMerchantOfferEditor(role)
    ? MERCHANT_LIVE_SAFE_EDIT_FIELDS
    : DEFAULT_LIVE_SAFE_EDIT_FIELDS;

export const getOfferEditProgressSaveFlags = ({
  role,
  isEdit,
  editOfferData,
  offerData,
  isApprovedProp,
}) => {
  const isLive = resolveOfferLiveState({
    isApprovedProp,
    editOfferData,
    offerData,
  });
  const isExistingEdit = Boolean(
    isEdit && (editOfferData?._id || offerData?._id),
  );
  const existingIsCreated =
    editOfferData?.isCreated ?? offerData?.isCreated ?? false;

  if (isMerchantOfferEditor(role) && isExistingEdit) {
    return {
      isCreated: existingIsCreated,
      preserveCurrentStatus: true,
      ...(isLive ? { status: "live" } : {}),
    };
  }

  if (isExistingEdit) {
    return {
      isCreated: existingIsCreated,
      preserveCurrentStatus: true,
      ...(isLive ? { status: "live" } : {}),
    };
  }

  return {
    isCreated: isLive,
    ...(isLive ? { status: "live" } : {}),
  };
};

export const shouldPreserveLiveOffer = ({
  currentPayload,
  originalOffer,
  isLive,
  role,
}) => {
  if (!isLive || !currentPayload || !originalOffer) return false;

  const canonicalCurrent = getCanonicalLiveOfferPayload(currentPayload, role);
  const canonicalOriginal = getCanonicalLiveOfferPayload(originalOffer, role);
  const liveSafeFields = getLiveOfferSafeFields(role);

  const changedKeys = [...LIVE_OFFER_COMPARISON_FIELDS].filter((key) => {
    if (shouldIgnoreField(key, currentPayload, originalOffer)) {
      return false;
    }

    const currentValue = normalizeOfferValue(key, canonicalCurrent?.[key]);
    const originalValue = normalizeOfferValue(key, canonicalOriginal?.[key]);
    return currentValue !== originalValue;
  });

  return (
    changedKeys.length === 0 ||
    changedKeys.every((key) => liveSafeFields.has(key))
  );
};

export const getLiveOfferSaveFlags = ({
  currentPayload,
  originalOffer,
  isLive,
  role,
  fallbackIsCreated = false,
  fallbackStatus = null,
}) => {
  const preserveLiveOffer = shouldPreserveLiveOffer({
    currentPayload,
    originalOffer,
    isLive,
    role,
  });

  return {
    preserveLiveOffer,
    isCreated: preserveLiveOffer ? true : fallbackIsCreated,
    status: preserveLiveOffer ? "live" : fallbackStatus,
  };
};

export const getOfferExitBehavior = ({
  currentPayload,
  originalOffer,
  isLive,
  role,
  isEdit,
  currentStatus,
  isLeaveSaveStep = true,
  fallbackIsCreated = false,
  fallbackStatus = null,
}) => {
  const liveSaveFlags = getLiveOfferSaveFlags({
    currentPayload,
    originalOffer,
    isLive,
    role,
    fallbackIsCreated,
    fallbackStatus,
  });

  const isMerchantEdit = isMerchantOfferEditor(role) && Boolean(isEdit);
  const isDeclinedStyleEdit =
    isMerchantEdit && isDeclinedStyleOfferStatus(currentStatus);
  const requiresResubmit =
    Boolean(isLeaveSaveStep) &&
    isMerchantEdit &&
    ((isLive && !liveSaveFlags.preserveLiveOffer) || isDeclinedStyleEdit);

  let mode = "save_progress";
  if (requiresResubmit) {
    mode = "resubmit";
  } else if (isMerchantEdit && isLive && liveSaveFlags.preserveLiveOffer) {
    mode = "save_live";
  }

  return {
    mode,
    requiresResubmit,
    isDeclinedStyleEdit,
    ...liveSaveFlags,
  };
};
